'use client'

/**
 * General breadcrumb component that works with any routing framework.
 *
 * @example
 * // Next.js (App Router) usage with queryOptions (recommended):
 * // Ensure trpc is set up (createTRPCReact + TRPCProvider with QueryClient)
 * // and app/api/trpc/[trpc]/route.ts exists. Then in a client component:
 *
 * 'use client'
 *
 * import { BreadcrumbData } from '@ui/web/components'
 * import Link from 'next/link'
 * import { usePathname, useSearchParams } from 'next/navigation'
 * import { trpc } from '~/lib/trpc'
 *
 * function AppBreadcrumb() {
 *   const pathname = usePathname()
 *   const searchParams = useSearchParams()
 *
 *   return (
 *     <BreadcrumbData
 *       pathname={pathname}
 *       searchParams={new URLSearchParams(searchParams.toString())}
 *       getQueryOptions={(id, type) => trpc.breadcrumb.getLabel.queryOptions({ id, type })}
 *       getHref={(segments) => `/${segments.join('/')}`}
 *       Link={Link}
 *     />
 *   )
 * }
 *
 * @example
 * // Next.js (App Router) usage with fetchLabel:
 * function AppBreadcrumbWithFetch() {
 *   const pathname = usePathname()
 *   const searchParams = useSearchParams()
 *   const utils = trpc.useUtils()
 *
 *   return (
 *     <BreadcrumbData
 *       pathname={pathname}
 *       searchParams={new URLSearchParams(searchParams.toString())}
 *       fetchLabel={async (id, type) => {
 *         const result = await utils.breadcrumb.getLabel.fetch({ id, type })
 *         return result ?? null
 *       }}
 *       getHref={(segments) => `/${segments.join('/')}`}
 *       Link={Link}
 *     />
 *   )
 * }
 *
 * // If your app uses an org prefix (e.g. /org_xyz/dashboard/...), strip it so
 * // segments start at "dashboard":
 * //   transformPathname={(path) => path.split('/').filter(Boolean).slice(1)}
 *
 * @example
 * // TanStack Start usage with queryOptions (recommended):
 * import { BreadcrumbData } from '@ui/web/components'
 * import { Link, useRouter } from '@tanstack/react-router'
 * import { useRouteContext } from '@tanstack/react-router'
 *
 * function MyBreadcrumb() {
 *   const router = useRouter()
 *   const { trpc } = useRouteContext({ from: '__root__' })
 *   const location = router.state.location
 *
 *   return (
 *     <BreadcrumbData
 *       pathname={location.pathname}
 *       searchParams={new URLSearchParams(location.search)}
 *       getQueryOptions={(id, type) => trpc.breadcrumb.getLabel.queryOptions({ id, type })}
 *       getHref={(segments) => `/${segments.join('/')}`}
 *       Link={Link}
 *     />
 *   )
 * }
 *
 * @example
 * // TanStack Start usage with fetchLabel:
 * function MyBreadcrumbWithFetch() {
 *   const router = useRouter()
 *   const { trpc } = useRouteContext({ from: '__root__' })
 *   const location = router.state.location
 *
 *   return (
 *     <BreadcrumbData
 *       pathname={location.pathname}
 *       searchParams={new URLSearchParams(location.search)}
 *       fetchLabel={async (id, type) => {
 *         const result = await trpc.breadcrumb.getLabel.query({ id, type })
 *         return result ?? null
 *       }}
 *       getHref={(segments) => `/${segments.join('/')}`}
 *       Link={Link}
 *     />
 *   )
 * }
 */
import * as React from 'react'
import { Fragment } from 'react'
import { useQueries } from '@tanstack/react-query'

import type { BreadcrumbType } from '@package/api'
import cn from '@config/tailwind/cn'

import {
  Breadcrumb as BreadcrumbComponent,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '../components'

function parseJsonArrayParam(value: string | string[] | null | undefined): string[] {
  if (value == null) return []
  const raw = Array.isArray(value) ? value[0] : value
  if (raw == null || raw === '') return []
  try {
    const decoded = decodeURIComponent(String(raw))
    let parsed: unknown = JSON.parse(decoded)
    // Handle double-encoded (e.g. from URL)
    if (typeof parsed === 'string') {
      parsed = JSON.parse(parsed) as string[]
    }
    return Array.isArray(parsed) ? (parsed as string[]).map(String) : []
  } catch (error) {
    console.error('[BreadcrumbData] Failed to parse param:', error)
    return []
  }
}

export interface BreadcrumbSegment {
  id: string
  type: BreadcrumbType | null
}

export interface BreadcrumbDataProps {
  className?: string
  pathname: string
  searchParams?: URLSearchParams | Record<string, string | string[] | null> | null
  /**
   * Function to fetch the label for a breadcrumb segment
   * Either this or getQueryOptions must be provided
   */
  fetchLabel?: (id: string, type: BreadcrumbType) => Promise<string | null>
  /**
   * Function to get query options for a breadcrumb segment (e.g., from tRPC)
   * Either this or fetchLabel must be provided
   * @example
   * getQueryOptions={(id, type) => trpc.breadcrumb.getLabel.queryOptions({ id, type })}
   */
  getQueryOptions?: (id: string, type: BreadcrumbType) => unknown
  /**
   * Function to generate the href for a breadcrumb link
   */
  getHref: (pathSegments: string[]) => string
  /**
   * Link component to use for navigation
   * Should accept href and children props
   */
  Link: React.ComponentType<{ href: string; children: React.ReactNode; className?: string }>
  /**
   * Optional function to transform pathname before processing
   * Useful for removing organization prefixes like "org_*"
   */
  transformPathname?: (pathname: string) => string[]
}

export function BreadcrumbData({
  className,
  pathname,
  searchParams,
  fetchLabel,
  getQueryOptions,
  getHref,
  Link,
  transformPathname,
}: BreadcrumbDataProps) {
  const pathSegments = React.useMemo(() => {
    const segments = pathname.split('/').filter((v) => v !== '')
    if (transformPathname) {
      return transformPathname(pathname)
    }
    return segments[0]?.startsWith('org_') ? segments.slice(1) : segments
  }, [pathname, transformPathname])

  const displaySegments = React.useMemo(() => {
    if (!pathSegments.length) return []
    return pathSegments
  }, [pathSegments])

  const types = React.useMemo(() => {
    if (!searchParams) return []
    const typeParam = searchParams instanceof URLSearchParams ? searchParams.get('type') : searchParams.type
    return parseJsonArrayParam(typeParam ?? undefined)
  }, [searchParams])

  const idsFromSearch = React.useMemo(() => {
    if (!searchParams) return []
    const idParam = searchParams instanceof URLSearchParams ? searchParams.get('id') : searchParams.id
    const parsed = parseJsonArrayParam(idParam ?? undefined)
    if (parsed.length > 0) return parsed
    // Single id (non-JSON string)
    const single =
      searchParams instanceof URLSearchParams
        ? searchParams.get('id')
        : Array.isArray(searchParams.id)
          ? searchParams.id[0]
          : searchParams.id
    return single != null && single !== '' ? [String(single)] : []
  }, [searchParams])

  const allIds = React.useMemo(() => {
    return displaySegments
      .map((segment, i) => {
        if (idsFromSearch.length > 0) {
          const id = idsFromSearch[i]
          if (id != null) return { id, type: types[i] }
        }
        if (segment.startsWith('user_')) return { id: segment, type: types[i] }
        return null
      })
      .filter((v): v is { id: string; type: string | undefined } => v !== null)
  }, [displaySegments, idsFromSearch, types])

  const allPathSegments = React.useMemo(() => {
    return displaySegments.map((v) => {
      const ids = allIds.find(({ id }) => id === v)

      if (ids) return { id: v, type: ids.type as BreadcrumbType | null }

      return { id: v, type: null }
    })
  }, [displaySegments, allIds])

  const queries = React.useMemo(() => {
    if (!fetchLabel && !getQueryOptions) {
      throw new Error('Either fetchLabel or getQueryOptions must be provided')
    }

    return allPathSegments.map((value, index) => {
      if (!value.type) {
        return {
          queryKey: ['breadcrumb', value, index] as const,
          queryFn: () => value.id,
          staleTime: 60 * 60 * 1000,
          enabled: false,
        }
      }

      // value.type is guaranteed to be non-null here
      const type = value.type

      if (getQueryOptions) {
        const queryOptions = getQueryOptions(value.id, type) as Record<string, unknown> & {
          queryKey?: readonly unknown[]
        }
        const result = {
          ...queryOptions,
          queryKey: queryOptions.queryKey ?? (['breadcrumb', value, index] as const),
          enabled: true, // Explicitly enable the query
        }

        return result
      }

      // Use fetchLabel as fallback
      return {
        queryKey: ['breadcrumb', value, index] as const,
        queryFn: async () => {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          const response = await fetchLabel!(value.id, type)
          return response ?? value.id
        },
        staleTime: 60 * 60 * 1000,
        enabled: true,
      }
    })
  }, [allPathSegments, fetchLabel, getQueryOptions])

  const results = useQueries({ queries })

  return (
    <BreadcrumbComponent className={cn('ui:hidden ui:md:flex', className)}>
      <BreadcrumbList>
        {displaySegments.map((segment, idx) => {
          const text = (() => {
            const result = results[idx]

            if (!result) return '...'

            if (result.isLoading) return '...'

            if (result.data) return result.data as string | null

            return segment
              .split('-')
              .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
              .join(' ')
          })()

          const link = getHref(pathSegments.slice(0, pathSegments.length - displaySegments.length + idx + 1))

          return (
            <Fragment key={`${idx}-${segment}`}>
              {idx !== displaySegments.length - 1 && (
                <BreadcrumbItem>
                  <BreadcrumbLink
                    render={(props) => (
                      <Link href={link} {...props}>
                        {text}
                      </Link>
                    )}
                  />
                </BreadcrumbItem>
              )}

              {idx !== displaySegments.length - 1 && <BreadcrumbSeparator />}

              {idx === displaySegments.length - 1 && (
                <BreadcrumbItem>
                  <BreadcrumbPage>{text}</BreadcrumbPage>
                </BreadcrumbItem>
              )}
            </Fragment>
          )
        })}
      </BreadcrumbList>
    </BreadcrumbComponent>
  )
}
